import { useTranslation } from 'react-i18next';
import type { SceneStore } from '@os/doc';
import { worldToScreen, type Camera } from '@os/engine';
import type { CommentThread } from '../../lib/comment-types';
import { useObject } from '../editor/useObject';
import { pinPosition } from './anchor';
import { cn, PinButton } from '../../ui';

type PinProps = { thread: CommentThread; store: SceneStore; camera: Camera; open: boolean; onOpen: (id: string) => void };

/** One pin; a pin on an object re-renders when that object moves, even when another user moved it. */
function Pin({ thread, store, camera, open, onOpen }: PinProps) {
  const { t } = useTranslation();
  const objectId = 'objectId' in thread.anchor ? thread.anchor.objectId : '';
  const object = useObject(store, objectId);
  const pos = pinPosition(thread.anchor, objectId ? object : undefined);
  const at = worldToScreen(camera, pos);
  const name = thread.first?.authorName || '—';
  return (
    <PinButton
      data-pin={thread.id}
      data-orphan={pos.orphan ? 'true' : undefined}
      label={t('comments.pin', { name })}
      count={thread.count}
      expanded={open}
      resolved={!!thread.resolvedAt}
      onClick={() => onOpen(thread.id)}
      style={{ transform: `translate(${at.x}px, ${at.y - 28}px)` }}
      className={cn('pointer-events-auto absolute left-0 top-0', pos.orphan && 'opacity-60')}
    />
  );
}

/** Comment pins in screen space, over the canvas and under the panels. */
export function CommentPins({ threads, store, camera, openId, onOpen }: { threads: readonly CommentThread[]; store: SceneStore; camera: Camera; openId: string | null; onOpen: (id: string) => void }) {
  return (
    <div className="pointer-events-none absolute inset-0 z-10 overflow-hidden">
      {threads.map((th) => (
        <Pin key={th.id} thread={th} store={store} camera={camera} open={th.id === openId} onOpen={onOpen} />
      ))}
    </div>
  );
}
